import React, { useEffect, useState } from "react"
import { apiUrl, getBackendUrl, setBackendUrl } from "../../api"
import "./admin.css"

const API_BASE = "/controller/reolink"
const WEATHER_API_BASE = "/controller/accuweather"
const emptyCamera = (index) => ({
     id: `camera-${index}`,
     name: `Kamera ${index}`,
     enabled: false,
     host: "",
     user: "",
     password: "",
     hasPassword: false,
     protocol: "rtsp",
     stream: "main",
     channel: "01",
     secondChannel: "02",
     rtspPath: "h264Preview",
     streamUrl: "",
     secondStreamUrl: "",
     duoMode: false,
     targetFps: 0,
})

function Admin() {
     const [settings, setSettings] = useState({
          host: "",
          user: "",
          password: "",
          protocol: "rtsp",
          stream: "main",
          channel: "01",
          rtspPath: "h264Preview",
          streamUrl: "",
          suggestedRtspUrls: [],
          candidates: [],
     })
     const [backendUrl, updateBackendUrl] = useState(getBackendUrl())
     const [cameras, setCameras] = useState([emptyCamera(1)])
     const [displaySettings, setDisplaySettings] = useState({
          defaultCameraId: "camera-1",
          hdmiEnabled: false,
     })
     const [weatherSettings, setWeatherSettings] = useState({
          locationName: "",
          locationKey: "",
          publicRadarUrl: "",
          user: "",
          password: "",
          apiKey: "",
          openWeatherApiKey: "",
          hasPassword: false,
          hasApiKey: false,
          hasOpenWeatherApiKey: false,
          radarProvider: "AccuWeather",
          radarEnabled: false,
     })
     const [status, setStatus] = useState("Bereit")
     const [weatherStatus, setWeatherStatus] = useState("Bereit")

     useEffect(() => {
          document.documentElement.classList.add("adminscrollroot")
          document.body.classList.add("adminscrollbody")
          return () => {
               document.documentElement.classList.remove("adminscrollroot")
               document.body.classList.remove("adminscrollbody")
          }
     }, [])

     useEffect(() => {
          loadSettings()
          loadCameraSettings()
          loadWeatherSettings()
     }, [])

     const loadSettings = () => {
          fetch(apiUrl(`${API_BASE}/settings`))
               .then((response) => response.json())
               .then((data) => {
                    setSettings((current) => ({
                         ...current,
                         ...data,
                         password: "",
                         suggestedRtspUrls: data.suggestedRtspUrls || [],
                         candidates: data.candidates || [],
                    }))
               })
               .catch(() => setStatus("Backend nicht erreichbar"))
     }

     const loadWeatherSettings = () => {
          fetch(apiUrl(`${WEATHER_API_BASE}/settings`))
               .then((response) => response.json())
               .then((data) => {
                    setWeatherSettings((current) => ({
                         ...current,
                         ...data,
                         password: "",
                         apiKey: "",
                         openWeatherApiKey: "",
                    }))
               })
               .catch(() => setWeatherStatus("Wetter-Settings nicht erreichbar"))
     }

     const loadCameraSettings = () => {
          fetch(apiUrl(`${API_BASE}/cameras/settings`))
               .then((response) => response.json())
               .then((data) => {
                    const nextCameras = data.cameras?.length ? data.cameras : [emptyCamera(1)]
                    setCameras(nextCameras)
                    setDisplaySettings({
                         defaultCameraId: data.defaultCameraId || nextCameras[0]?.id || "camera-1",
                         hdmiEnabled: Boolean(data.hdmiEnabled),
                    })
               })
               .catch(() => setStatus("Kamera-Liste nicht erreichbar"))
     }

     const discover = () => {
          setStatus("Suche Kameras")
          fetch(apiUrl(`${API_BASE}/discover`), { method: "POST" })
               .then((response) => response.json())
               .then((data) => {
                    setSettings((current) => ({
                         ...current,
                         host: data.host || current.host,
                         candidates: data.candidates || [],
                    }))
                    setStatus(data.message || "Suche abgeschlossen")
               })
               .catch(() => setStatus("Suche fehlgeschlagen"))
     }

     const save = (event) => {
          event.preventDefault()
          if (!settings.host?.trim() && !settings.streamUrl?.trim()) {
               setStatus("Host oder Stream URL fehlt")
               return
          }
          if (!settings.user?.trim()) {
               setStatus("User fehlt")
               return
          }
          if (!settings.password?.trim() && !settings.hasPassword) {
               setStatus("Passwort fehlt")
               return
          }
          setStatus("Speichere")
          fetch(apiUrl(`${API_BASE}/settings`), {
               method: "POST",
               headers: {
                    "Content-Type": "application/json",
               },
               body: JSON.stringify(settings),
          })
               .then((response) => response.json())
               .then((data) => {
                    setSettings((current) => ({
                         ...current,
                         ...data,
                         password: "",
                         suggestedRtspUrls: data.suggestedRtspUrls || current.suggestedRtspUrls || [],
                    }))
                    setStatus("Gespeichert")
               })
               .catch(() => setStatus("Speichern fehlgeschlagen"))
     }

     const saveBackend = () => {
          setBackendUrl(backendUrl)
          setStatus("Backend URL gespeichert")
          loadSettings()
          loadCameraSettings()
          loadWeatherSettings()
     }

     const saveCameras = (event) => {
          event.preventDefault()
          setStatus("Speichere Kameras")
          fetch(apiUrl(`${API_BASE}/cameras/settings`), {
               method: "POST",
               headers: {
                    "Content-Type": "application/json",
               },
               body: JSON.stringify({ cameras, ...displaySettings }),
          })
               .then((response) => response.json())
               .then((data) => {
                    setCameras(data.cameras?.length ? data.cameras : [emptyCamera(1)])
                    setDisplaySettings({
                         defaultCameraId: data.defaultCameraId || data.cameras?.[0]?.id || "camera-1",
                         hdmiEnabled: Boolean(data.hdmiEnabled),
                    })
                    setStatus("Kameras gespeichert")
               })
               .catch(() => setStatus("Kameras speichern fehlgeschlagen"))
     }

     const saveWeather = (event) => {
          event.preventDefault()
          if (!weatherSettings.locationKey?.trim()) {
               setWeatherStatus("Location-ID fehlt")
               return
          }
          if (!weatherSettings.locationName?.trim()) {
               setWeatherStatus("Ort fehlt")
               return
          }
          if (!weatherSettings.apiKey?.trim() && !weatherSettings.hasApiKey) {
               setWeatherStatus("AccuWeather API-Key fehlt")
               return
          }

          setWeatherStatus("Speichere")
          fetch(apiUrl(`${WEATHER_API_BASE}/settings`), {
               method: "POST",
               headers: {
                    "Content-Type": "application/json",
               },
               body: JSON.stringify(weatherSettings),
          })
               .then((response) => response.json())
               .then((data) => {
                    setWeatherSettings((current) => ({
                         ...current,
                         ...data,
                         password: "",
                         apiKey: "",
                         openWeatherApiKey: "",
                    }))
                    setWeatherStatus("Gespeichert")
               })
               .catch(() => setWeatherStatus("Speichern fehlgeschlagen"))
     }

     const update = (field, value) => {
          setSettings((current) => ({
               ...current,
               [field]: value,
          }))
     }

     const updateWeather = (field, value) => {
          setWeatherSettings((current) => ({
               ...current,
               [field]: value,
          }))
     }

     const updateCamera = (index, field, value) => {
          setCameras((current) => current.map((camera, cameraIndex) => (
               cameraIndex === index ? { ...camera, [field]: value } : camera
          )))
     }

     const addCamera = () => {
          setCameras((current) => [...current, emptyCamera(current.length + 1)])
     }

     const removeCamera = (index) => {
          setCameras((current) => current.filter((camera, cameraIndex) => cameraIndex !== index))
     }

     const updateDisplay = (field, value) => {
          setDisplaySettings((current) => ({
               ...current,
               [field]: value,
          }))
     }

     const selectCandidate = (host) => {
          setSettings((current) => ({
               ...current,
               host,
               streamUrl: "",
          }))
     }

     return (
          <main className="adminpage">
               <section className="adminpanel">
                    <header className="adminheader">
                         <p>Casa Giu</p>
                         <h1>Admin</h1>
                    </header>

                    <form className="adminform" onSubmit={save}>
                         <h2>Kamera</h2>
                         <label>
                              Backend URL
                              <input value={backendUrl} onChange={(event) => updateBackendUrl(event.target.value)} placeholder="https://backend.example.com" />
                         </label>

                         <div className="adminactions compactactions">
                              <button type="button" onClick={saveBackend}>Backend speichern</button>
                         </div>

                         <label>
                              Host
                              <input value={settings.host || ""} onChange={(event) => update("host", event.target.value)} placeholder="192.168.0.55" />
                         </label>

                         <label>
                              Externe Stream URL
                              <input value={settings.streamUrl || ""} onChange={(event) => update("streamUrl", event.target.value)} placeholder="rtsp://user:pass@example.com:554/Preview_01_main" />
                         </label>

                         {settings.candidates.length > 0 && (
                              <div className="candidategrid">
                                   {settings.candidates.map((candidate) => (
                                        <button type="button" key={candidate} onClick={() => selectCandidate(candidate)}>
                                            {candidate}
                                        </button>
                                   ))}
                              </div>
                         )}

                         <label>
                              User
                              <input value={settings.user || ""} onChange={(event) => update("user", event.target.value)} autoComplete="username" />
                         </label>

                         <label>
                              Passwort
                              <input value={settings.password || ""} onChange={(event) => update("password", event.target.value)} type="password" autoComplete="current-password" />
                         </label>

                         <div className="adminrow">
                              <label>
                                   Protokoll
                                   <select value={settings.protocol || "rtsp"} onChange={(event) => update("protocol", event.target.value)}>
                                        <option value="rtsp">RTSP</option>
                                        <option value="rtmp">RTMP</option>
                                   </select>
                              </label>

                              <label>
                                   Kamera-Kanal
                                   <select value={settings.channel || "01"} onChange={(event) => update("channel", event.target.value)}>
                                        <option value="01">Kanal 01</option>
                                        <option value="02">Kanal 02</option>
                                        <option value="03">Kanal 03</option>
                                        <option value="04">Kanal 04</option>
                                   </select>
                              </label>
                         </div>

                         <div className="adminrow">
                              <label>
                                   RTSP URL-Muster
                                   <select value={settings.rtspPath || "h264Preview"} onChange={(event) => update("rtspPath", event.target.value)}>
                                        <option value="h264Preview">h264Preview</option>
                                        <option value="Preview">Preview</option>
                                   </select>
                              </label>

                              <label>
                                   Stream
                                   <select value={settings.stream || "main"} onChange={(event) => update("stream", event.target.value)}>
                                        <option value="main">Main</option>
                                        <option value="sub">Sub</option>
                                   </select>
                              </label>
                         </div>

                         {settings.suggestedRtspUrls?.length > 0 && (
                              <div className="urlgrid">
                                   {settings.suggestedRtspUrls.map((url) => (
                                        <button type="button" key={url} onClick={() => update("streamUrl", url)}>
                                             {url}
                                        </button>
                                   ))}
                              </div>
                         )}

                         <div className="adminactions">
                              <button type="button" onClick={discover}>Suchen</button>
                              <button type="submit">Speichern</button>
                         </div>

                         <p className="adminstatus">{status}</p>
                    </form>

                    <form className="adminform weatheradminform" onSubmit={saveCameras}>
                         <h2>Kameras</h2>
                         <div className="adminrow">
                              <label>
                                   Standard fuer /
                                   <select value={displaySettings.defaultCameraId || cameras[0]?.id || ""} onChange={(event) => updateDisplay("defaultCameraId", event.target.value)}>
                                        {cameras.map((camera, index) => (
                                             <option key={camera.id || index} value={camera.id || `camera-${index + 1}`}>
                                                  {camera.name || camera.id || `Kamera ${index + 1}`}
                                             </option>
                                        ))}
                                   </select>
                              </label>

                              <label className="admincheck">
                                   <input checked={displaySettings.hdmiEnabled} onChange={(event) => updateDisplay("hdmiEnabled", event.target.checked)} type="checkbox" />
                                   HDMI-Kiosk aktivieren
                              </label>
                         </div>

                         {cameras.map((camera, index) => (
                              <div className="cameraconfig" key={camera.id || index}>
                                   <div className="adminrow">
                                        <label>
                                             Name
                                             <input value={camera.name || ""} onChange={(event) => updateCamera(index, "name", event.target.value)} />
                                        </label>

                                        <label>
                                             ID
                                             <input value={camera.id || ""} onChange={(event) => updateCamera(index, "id", event.target.value)} />
                                        </label>
                                   </div>

                                   <label className="admincheck admincheckwide">
                                        <input checked={Boolean(camera.enabled)} onChange={(event) => updateCamera(index, "enabled", event.target.checked)} type="checkbox" />
                                        Kamera-Stream aktivieren
                                   </label>

                                   <label>
                                        Host
                                        <input value={camera.host || ""} onChange={(event) => updateCamera(index, "host", event.target.value)} placeholder="192.168.0.55" />
                                   </label>

                                   <label>
                                        Externe Stream URL
                                        <input value={camera.streamUrl || ""} onChange={(event) => updateCamera(index, "streamUrl", event.target.value)} placeholder="rtsp://user:pass@example.com:554/Preview_01_main" />
                                   </label>

                                   <label>
                                        Zweite Stream URL
                                        <input value={camera.secondStreamUrl || ""} onChange={(event) => updateCamera(index, "secondStreamUrl", event.target.value)} placeholder="optional fuer Duo/Linse 2" />
                                   </label>

                                   <div className="adminrow">
                                        <label>
                                             User
                                             <input value={camera.user || ""} onChange={(event) => updateCamera(index, "user", event.target.value)} autoComplete="username" />
                                        </label>

                                        <label>
                                             Passwort
                                             <input value={camera.password || ""} onChange={(event) => updateCamera(index, "password", event.target.value)} type="password" autoComplete="current-password" placeholder={camera.hasPassword ? "Gespeichert" : ""} />
                                        </label>
                                   </div>

                                   <div className="adminrow">
                                        <label>
                                             Stream
                                             <select value={camera.stream || "main"} onChange={(event) => updateCamera(index, "stream", event.target.value)}>
                                                  <option value="main">Main</option>
                                                  <option value="sub">Sub</option>
                                             </select>
                                        </label>

                                        <label>
                                             Kanal
                                             <select value={camera.channel || "01"} onChange={(event) => updateCamera(index, "channel", event.target.value)}>
                                                  <option value="01">Kanal 01</option>
                                                  <option value="02">Kanal 02</option>
                                                  <option value="03">Kanal 03</option>
                                                  <option value="04">Kanal 04</option>
                                             </select>
                                        </label>

                                        <label>
                                             Zweiter Kanal
                                             <select value={camera.secondChannel || "02"} onChange={(event) => updateCamera(index, "secondChannel", event.target.value)}>
                                                  <option value="01">Kanal 01</option>
                                                  <option value="02">Kanal 02</option>
                                                  <option value="03">Kanal 03</option>
                                                  <option value="04">Kanal 04</option>
                                             </select>
                                        </label>
                                   </div>

                                   <div className="adminrow">
                                        <label className="admincheck">
                                             <input checked={Boolean(camera.duoMode)} onChange={(event) => updateCamera(index, "duoMode", event.target.checked)} type="checkbox" />
                                             Duo 4G zusammenfuegen
                                        </label>

                                        <label>
                                             FPS Limit
                                             <select value={camera.targetFps || 0} onChange={(event) => updateCamera(index, "targetFps", Number(event.target.value))}>
                                                  <option value={0}>Copy / Original</option>
                                                  <option value={1}>1 fps</option>
                                                  <option value={2}>2 fps</option>
                                                  <option value={3}>3 fps</option>
                                                  <option value={5}>5 fps</option>
                                                  <option value={10}>10 fps</option>
                                             </select>
                                        </label>
                                   </div>

                                   <div className="adminactions compactactions">
                                        <button type="button" onClick={() => removeCamera(index)} disabled={cameras.length === 1}>Entfernen</button>
                                   </div>
                              </div>
                         ))}

                         <div className="adminactions">
                              <button type="button" onClick={addCamera}>+ Kamera</button>
                              <button type="submit">Kameras speichern</button>
                         </div>
                    </form>

                    <form className="adminform weatheradminform" onSubmit={saveWeather}>
                         <h2>Wetter</h2>
                         <div className="adminrow">
                              <label>
                                   Ort
                                   <input value={weatherSettings.locationName || ""} onChange={(event) => updateWeather("locationName", event.target.value)} placeholder="Vibo Marina, Calabria" />
                              </label>

                              <label>
                                   AccuWeather Location-ID
                                   <input value={weatherSettings.locationKey || ""} onChange={(event) => updateWeather("locationKey", event.target.value)} placeholder="1544189" />
                              </label>
                         </div>

                         <label>
                              Radar URL
                              <input value={weatherSettings.publicRadarUrl || ""} onChange={(event) => updateWeather("publicRadarUrl", event.target.value)} placeholder="https://www.accuweather.com/..." />
                         </label>

                         <label className="admincheck">
                              <input checked={Boolean(weatherSettings.radarEnabled)} onChange={(event) => updateWeather("radarEnabled", event.target.checked)} type="checkbox" />
                              Radar-Sequenz jede Minute einblenden
                         </label>

                         <div className="adminrow">
                              <label>
                                   Wetter User
                                   <input value={weatherSettings.user || ""} onChange={(event) => updateWeather("user", event.target.value)} autoComplete="username" />
                              </label>

                              <label>
                                   Wetter Passwort
                                   <input value={weatherSettings.password || ""} onChange={(event) => updateWeather("password", event.target.value)} type="password" autoComplete="current-password" placeholder={weatherSettings.hasPassword ? "Gespeichert" : ""} />
                              </label>
                         </div>

                         <label>
                              AccuWeather API-Key
                              <input value={weatherSettings.apiKey || ""} onChange={(event) => updateWeather("apiKey", event.target.value)} type="password" placeholder={weatherSettings.hasApiKey ? "Gespeichert" : ""} />
                         </label>

                         <label>
                              OpenWeatherMap API-Key fuer Radar
                              <input value={weatherSettings.openWeatherApiKey || ""} onChange={(event) => updateWeather("openWeatherApiKey", event.target.value)} type="password" placeholder={weatherSettings.hasOpenWeatherApiKey ? "Gespeichert" : ""} />
                         </label>

                         <p className="adminstatus">Radarprovider: {weatherSettings.radarProvider || "AccuWeather"}</p>

                         <div className="adminactions">
                              <button type="submit">Wetter speichern</button>
                         </div>

                         <p className="adminstatus">{weatherStatus}</p>
                    </form>
               </section>
          </main>
     )
}

export default Admin
